import {
  AnimatedPage,
  Flex,
  FlexAnchor,
  Header,
  HeaderTitle,
  Icon,
  LinkAnchor,
  MainSection,
  PrimaryButton,
  SideSection,
  StickySidebar,
  WideContainer,
} from '@wrisk/ui-components'
import { isNil } from 'lodash-es'
import React, { FunctionComponent, useCallback } from 'react'
import { Link, useNavigate } from 'react-router'

import { CoverOption, getCurrentQuote, isAddOn } from '../../../../../domain'
import { useCoverOptions } from '../../../../../hooks/covers'
import { useErrorHandlingAsyncCallback } from '../../../../../hooks/errors'
import {
  tDefaults,
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { useUpButton } from '../../../../../infrastructure/routing'
import { AddOnSelector } from '../../../../organisms/covers'
import { Footer } from '../../../../templates'
import { useProduct } from '../../../ProductProvider'
import { useProposal } from '../../ProposalProvider'
import { useCheckout } from '../CheckoutProvider'
import { ProposalAddOnsSidebar } from './ProposalAddOnsSidebar'

const tKey = TKey('pages.proposal-add-ons')

export const ProposalAddOnsPage: FunctionComponent = () => {
  const { t } = useWriskTranslation()

  const navigate = useNavigate()

  const { previous, next } = useCheckout()
  const { proposal, commit, dispatch } = useProposal()
  const { product } = useProduct()

  const upButton = useUpButton(previous)

  const quote = getCurrentQuote(proposal)

  const optionalCovers = useCoverOptions(quote, product).filter(isAddOn(product))

  const disabled = optionalCovers.some((it) => isNil(it.active) && it.available)

  const { loading, execute: onContinue } = useErrorHandlingAsyncCallback(async () => {
    await commit()
    await navigate(next.url)
  })

  const onChange = useCallback(
    (coverOption: CoverOption) => {
      dispatch({
        type: 'UPDATE_COVER_ITEM',
        payload: {
          coverOption: coverOption.coverOption,
          active: coverOption.active,
        },
      })
    },
    [quote],
  )

  return (
    <AnimatedPage pageId='add-ons-page' footer={<Footer />}>
      <WideContainer>
        <Header>
          <Flex width={1}>
            <FlexAnchor
              fontFamily='buttons'
              mb={10}
              as={Link}
              to={upButton?.to ?? '/'}
              data-testid='link-back'
            >
              <Icon size='small' pl={2} icon='chevronRight' rotate={180} />
              <LinkAnchor variant='standalone' as='span' typoSize='sm'>
                {upButton?.text || 'Back'}
              </LinkAnchor>
            </FlexAnchor>
          </Flex>
          <HeaderTitle>{t(tKey('header'))}</HeaderTitle>
        </Header>
      </WideContainer>
      <WideContainer variant='sidebar'>
        <StickySidebar>
          <SideSection>
            <ProposalAddOnsSidebar
              onContinue={onContinue}
              disabled={disabled}
              loading={loading}
            />
          </SideSection>
        </StickySidebar>
        <MainSection>
          <AddOnSelector
            quote={quote}
            coverOptions={optionalCovers}
            onChange={onChange}
          />
          <ProposalAddOnsSidebar
            displayOn='mobile'
            disabled={disabled}
            loading={loading}
            onContinue={onContinue}
          />

          <Flex displayOn='desktop' width={1} mt={5} data-testid='button-continue'>
            <PrimaryButton
              layout='fixed'
              loading={loading}
              disabled={disabled}
              onClick={onContinue}
              data-testid='button-continue'
            >
              {t([tKey('actions.continue'), tDefaults('actions.continue')])}
            </PrimaryButton>
          </Flex>
        </MainSection>
      </WideContainer>
    </AnimatedPage>
  )
}
